import { ITeamRepository } from '../../domain/repositories/ITeamRepository';
import { ConfigManager } from '../../application/config/Config';
import { LoggerFactory } from '../../application/logging/Logger';
import { InternalServerError } from '../../application/errors/AppError';
import { TeamPost } from '@/types';
import {
  NormalResponse,
  TeamData,
  TeamProfileResponse,
  ProfileResponse,
  ImageUploadResponse,
  UserConnectionStatus,
  TeamMemberStatus
} from '../../types/Team';

/**
 * API-based Team Repository Implementation
 * Based on C# TeamService implementation
 * Follows Repository Pattern and Dependency Inversion Principle
 */
export class TeamRepository implements ITeamRepository {
  private readonly config = ConfigManager.getInstance().getApiConfig();
  private readonly logger = LoggerFactory.getLogger();

async publishTeamPost(
  teamId: number,
  userId: number,
  coachId: number,
  postText: string,
  postPhoto: string,
  postVideo: string
): Promise<number> 
{
  const url = `${this.config.baseUrl}/teams/publish_team_post`;

  try {
    const body = new URLSearchParams();
    body.append("team_id", String(teamId));
    body.append("user_id", String(userId));
    body.append("coach_id", String(coachId));
    body.append("post_text", postText);
    body.append("post_photo", postPhoto);
    body.append("post_video", postVideo);

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-API-Key": this.config.apiKey,
        "Authorization": `Bearer ${localStorage.getItem("auth_token")}`,
      },
      body,
    });

    const text = await response.text();
    const json = JSON.parse(text);

    return json.status ?? 0;
  } catch (err) {
    console.error("publishTeamPost error:", err);
    return 0;
  }
}

async uploadTeamPostImage(
  file: File,
  onProgress?: (percent: number) => void
): Promise<any> {

  const url = `${this.config.baseUrl}/teams/upload_team_post_image`;

  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);

    // Track upload progress
    xhr.upload.onprogress = (evt) => {
      if (evt.lengthComputable && onProgress) {
        onProgress(Math.round((evt.loaded / evt.total) * 100));
      }
    };

    xhr.onload = () => resolve(JSON.parse(xhr.responseText));
    xhr.onerror = () => resolve({ status: 0 });

    const form = new FormData();
    form.append("type", "photo");
    form.append("image", file);

    xhr.setRequestHeader("X-API-Key", this.config.apiKey);
    xhr.setRequestHeader(
      "Authorization",
      `Bearer ${localStorage.getItem("auth_token")}`
    );

    xhr.send(form);
  });
}
async uploadTeamPostVideo(
  file: File,
  onProgress?: (percent: number) => void
): Promise<any> {

  const url = `${this.config.baseUrl}/teams/upload_team_post_video`;

  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);

    xhr.upload.onprogress = (evt) => {
      if (evt.lengthComputable && onProgress) {
        onProgress(Math.round((evt.loaded / evt.total) * 100));
      }
    };

    xhr.onload = () => resolve(JSON.parse(xhr.responseText));
    xhr.onerror = () => resolve({ status: 0 });

    const form = new FormData();
    form.append("video", file);

    xhr.setRequestHeader("X-API-Key", this.config.apiKey);
    xhr.setRequestHeader(
      "Authorization",
      `Bearer ${localStorage.getItem("auth_token")}`
    );

    xhr.send(form);
  });
}


async fetchTeamPosts(
  teamId: number,
  userId: number,
  coachId: number,
  page: number
): Promise<TeamPost[]> {
  const url = `${this.config.baseUrl}/teams/fetch_team_posts`;

  try {
    const body = new URLSearchParams();
    body.append("team_id", String(teamId));
    body.append("user_id", String(userId));
    body.append("coach_id", String(coachId));
    body.append("page", String(page));

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-API-Key": this.config.apiKey,
        "Authorization": `Bearer ${localStorage.getItem("auth_token")}`
      },
      body
    });

    const text = await response.text();
    if (!text) return [];

    const json = JSON.parse(text);

    // IMPORTANT — backend sends string JSON
    const parsed = json.data ? JSON.parse(json.data) : [];

    return parsed as TeamPost[];

  } catch (err) {
    console.error("fetchTeamPosts error:", err);
    return [];
  }
}

async deleteTeamPost(
  teamFeedId: number,
  teamId: number,
  userId: number
): Promise<number> {
  const url = `${this.config.baseUrl}/teams/delete_team_post`;

  try {
    const body = new URLSearchParams();
    body.append("team_feed_id", String(teamFeedId));
    body.append("team_id", String(teamId));
    body.append("user_id", String(userId));

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-API-Key": this.config.apiKey,
        "Authorization": `Bearer ${localStorage.getItem("auth_token")}`,
      },
      body,
    });

    const text = await response.text();
    if (!text) return 0;

    const json = JSON.parse(text);
    return json.status ?? 0;
  } catch (err) {
    this.logger.error(
      "Failed to delete team post",
      err as Error,
      { teamFeedId, teamId, userId }
    );
    return 0;
  }
}

  // Team Management (API-based)
  async getSwimmerTeams(userId: number): Promise<TeamData> {
  const url = `${this.config.baseUrl}/teams/get_swimmer_teams`;

  try {
    const body = new URLSearchParams();
    body.append("user_id", String(userId));

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-API-Key": this.config.apiKey,
        "Authorization": `Bearer ${localStorage.getItem("auth_token")}`
      },
      body
    });

    const text = await response.text();
    if (!text) {
      return { success: false, message: "Empty response", data: [] };
    }

    const json = JSON.parse(text);

    const list = json.data ? JSON.parse(json.data) : [];

    return {
      success: json.status === 1,
      message: json.msg || "",
      data: list
    };

  } catch (error) {
    console.error("getSwimmerTeams error:", error);
    return { success: false, message: "Exception occurred", data: [] };
  }
  }

  async getCoachTeams(coachId: number, status: number): Promise<TeamData> {
  const url = `${this.config.baseUrl}/teams/get_coach_teams`;

  try {
    const body = new URLSearchParams();
    body.append("coach_id", String(coachId));
    body.append("status", String(status));

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-API-Key": this.config.apiKey,
        "Authorization": `Bearer ${localStorage.getItem("auth_token")}`
      },
      body
    });

    const text = await response.text();
    if (!text) {
      return { success: false, message: "Empty response", data: [] };
    }

    const json = JSON.parse(text);

    const list = json.data ? JSON.parse(json.data) : [];

    return {
      success: json.status === 1,
      message: json.msg || "",
      data: list
    };

  } catch (error) {
    console.error("getCoachTeams error:", error);
    return { success: false, message: "Exception occurred", data: [] };
  }
}


async addTeamMember(
  coachId: number,
  teamId: number,
  userIds: number[]
): Promise<NormalResponse> {

  const url = `${this.config.baseUrl}/teams/add_team_member`;

  try {
    const body = new URLSearchParams();
    body.append("coach_id", String(coachId));
    body.append("team_id", String(teamId));

    userIds.forEach((id) => {
      body.append("user_ids[]", String(id));
    });

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-API-Key": this.config.apiKey,
        "Authorization": `Bearer ${localStorage.getItem("auth_token")}`
      },
      body
    });

    const raw = await response.text();

    if (!raw) {
      return { success: false, message: "Empty response", data: null };
    }

    const json = JSON.parse(raw);

    return {
      success: json.status === 1,
      message: json.msg || "",
      data: json.data
    };

  } catch (err) {
    console.error("addTeamMember error:", err);

    return {
      success: false,
      message: "Exception occurred",
      data: null
    };
  }
}


async createTeam(
  coachId: number,
  name: string,
  city: string,
  state: string,
  country: string,
  info: string,
  profileImage: string,
  coverImage: string
): Promise<TeamProfileResponse> {
  
  const url = `${this.config.baseUrl}/teams/create_team`;

  const emptyData = {
    team_id: 0,
    name: "",
    city: "",
    state: "",
    country: "",
    info: "",
    profile_image: "",
    cover_image: "",
    created_at: "",
    updated_at: ""
  };

  try {
    const body = new URLSearchParams();
    body.append("coach_id", String(coachId));
    body.append("name", name);
    body.append("city", city);
    body.append("state", state);
    body.append("country", country);
    body.append("info", info);
    body.append("profile_image", profileImage);
    body.append("cover_image", coverImage);

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-API-Key": this.config.apiKey,
        "Authorization": `Bearer ${localStorage.getItem("auth_token")}`
      },
      body,
    });

    const raw = await response.text();
    if (!raw) {
      return {
        success: false,
        message: "Empty response",
        data: emptyData
      };
    }

    const json = JSON.parse(raw);

    return {
      success: json.status === 1,
      message: json.msg || "",
      data: json.data || emptyData
    };

  } catch (err) {
    console.error("createTeam error:", err);
    return {
      success: false,
      message: "Exception occurred",
      data: emptyData
    };
  }
}

async updateTeam(
  coachId: number,
  teamId: number,
  name: string,
  city: string,
  state: string,
  country: string,
  info: string,
  profileImage: string,
  coverImage: string
): Promise<TeamProfileResponse> {
  const url = `${this.config.baseUrl}/teams/update_team`;

  const emptyData = {
    team_id: 0,
    name: "",
    city: "",
    state: "",
    country: "",
    info: "",
    profile_image: "",
    cover_image: "",
    created_at: "",
    updated_at: ""
  };

  try {
    const body = new URLSearchParams();
    body.append("coach_id", String(coachId));
    body.append("team_id", String(teamId));
    body.append("name", name);
    body.append("city", city);
    body.append("state", state);
    body.append("country", country);
    body.append("info", info);
    body.append("profile_image", profileImage);
    body.append("cover_image", coverImage);

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-API-Key": this.config.apiKey,
        "Authorization": `Bearer ${localStorage.getItem("auth_token")}`
      },
      body,
    });

    const raw = await response.text();
    if (!raw) {
      return {
        success: false,
        message: "Empty response",
        data: emptyData
      };
    }

    const json = JSON.parse(raw);

    return {
      success: json.status === 1,
      message: json.msg || "",
      data: json.data || emptyData
    };
  } catch (err) {
    console.error("updateTeam error:", err);
    return {
      success: false,
      message: "Exception occurred",
      data: emptyData
    };
  }
}

async uploadTeamImage(
  file: File,
  imageMode: "profile" | "cover"
): Promise<ImageUploadResponse> {
  const url = `${this.config.baseUrl}/teams/upload_team_image`;
  try {
    const formData = new FormData();
    formData.append("image", file);
    formData.append("image_mode", imageMode);

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "X-API-Key": this.config.apiKey,
        "Authorization": `Bearer ${localStorage.getItem("auth_token")}`
      },
      body: formData,
    });

    const raw = await response.text();
    if (!raw) {
      return { status: 0, msg: "Empty response", image_link: "" };
    }
    const json = JSON.parse(raw);
    return {
      status: Number(json.status ?? 0),
      msg: json.msg || "",
      image_link: json.image_link || json.data?.image_link || "",
    };
  } catch (err) {
    console.error("uploadTeamImage error:", err);
    return { status: 0, msg: "Exception occurred", image_link: "" };
  }
}

async deleteTeam(
  teamId: number,
  coachId: number
): Promise<NormalResponse> {
  const url = `${this.config.baseUrl}/teams/team_delete`;

  try {
    const body = new URLSearchParams();
    body.append("team_id", String(teamId));
    body.append("coach_id", String(coachId));

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-API-Key": this.config.apiKey,
        "Authorization": `Bearer ${localStorage.getItem("auth_token")}`,
      },
      body,
    });

    const text = await response.text();
    if (!text) {
      return {
        success: false,
        message: "Empty response",
        data: null,
      };
    }

    const json = JSON.parse(text);

    return {
      success: json.status === 1,
      message: json.msg || "",
      data: json.data ?? null,
    };
  } catch (err) {
    this.logger.error(
      "Failed to delete team",
      err as Error,
      { teamId, coachId }
    );

    return {
      success: false,
      message: "Exception occurred",
      data: null,
    };
  }
}

  async createTeamApi(coachId: number, name: string, city: string, state: string, country: string, info: string, profileImage: string, coverImage: string): Promise<TeamProfileResponse> {
    try {
      const formData = new FormData();
      formData.append('coach_id', coachId.toString());
      formData.append('name', name);
      formData.append('city', city);
      formData.append('state', state);
      formData.append('country', country);
      formData.append('info', info);
      formData.append('profile_image', profileImage);
      formData.append('cover_image', coverImage);

      const response = await fetch(`${this.config.baseUrl}/api/team/create-team`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to create team');
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to create team', error as Error, { coachId, name });
      throw error;
    }
  }

  async updateTeamApi(coachId: number, teamId: number, name: string, city: string, state: string, country: string, info: string, profileImage: string, coverImage: string): Promise<TeamProfileResponse> {
    try {
      const formData = new FormData();
      formData.append('coach_id', coachId.toString());
      formData.append('team_id', teamId.toString());
      formData.append('name', name);
      formData.append('city', city);
      formData.append('state', state);
      formData.append('country', country);
      formData.append('info', info);
      formData.append('profile_image', profileImage);
      formData.append('cover_image', coverImage);

      const response = await fetch(`${this.config.baseUrl}/api/team/update-team`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to update team');
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to update team', error as Error, { coachId, teamId, name });
      throw error;
    }
  }

  async deleteTeamApi(teamId: number, coachId: number): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('team_id', teamId.toString());
      formData.append('coach_id', coachId.toString());

      const response = await fetch(`${this.config.baseUrl}/api/team/delete-team`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to delete team');
      }

      return await response.text();
    } catch (error) {
      this.logger.error('Failed to delete team', error as Error, { teamId, coachId });
      throw error;
    }
  }

  // Team Members (API-based)
  async addTeamMemberApi(coachId: number, teamId: number, userIds: number[]): Promise<NormalResponse> {
    try {
      const formData = new FormData();
      formData.append('coach_id', coachId.toString());
      formData.append('team_id', teamId.toString());
      userIds.forEach(userId => {
        formData.append('user_ids[]', userId.toString());
      });

      const response = await fetch(`${this.config.baseUrl}/api/team/add-team-member`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to add team member');
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to add team member', error as Error, { coachId, teamId, userIds });
      throw error;
    }
  }

  async getTeamSwimmersApi(teamId: number, status: TeamMemberStatus): Promise<NormalResponse> {
    try {
      const formData = new FormData();
      formData.append('team_id', teamId.toString());
      formData.append('status', status.toString());

      const response = await fetch(`${this.config.baseUrl}/api/team/get-team-swimmers`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to get team swimmers');
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to get team swimmers', error as Error, { teamId, status });
      throw error;
    }
  }

  async removeSwimmerFromTeamApi(teamId: number, swimmerId: number, coachId: number): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('team_id', teamId.toString());
      formData.append('swimmer_id', swimmerId.toString());
      formData.append('coach_id', coachId.toString());

      const response = await fetch(`${this.config.baseUrl}/api/team/remove-swimmer-from-team`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to remove swimmer from team');
      }

      return await response.text();
    } catch (error) {
      this.logger.error('Failed to remove swimmer from team', error as Error, { teamId, swimmerId, coachId });
      throw error;
    }
  }

  // User Management (API-based)
  async getUserApi(userId: number, forceRefresh: boolean = false): Promise<ProfileResponse> {
    try {
      const response = await fetch(`${this.config.baseUrl}/api/team/get-user/${userId}?forceRefresh=${forceRefresh}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to get user');
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to get user', error as Error, { userId, forceRefresh });
      throw error;
    }
  }

  async getCoachSwimmersApi(coachId: number, status: UserConnectionStatus): Promise<NormalResponse> {
    try {
      const url = `${this.config.baseUrl}/coaches/get_coach_swimmers`;

      const body = new URLSearchParams();
      body.append("coach_id", coachId.toString());
      body.append("status", status.toString());

      const headers = {
        "X-API-Key": this.config.apiKey,
        "Authorization": `Bearer ${localStorage.getItem("auth_token")}`,
        "Content-Type": "application/x-www-form-urlencoded",
      };

      const response = await fetch(url, {
        method: "POST",
        headers,
        body: body.toString(),
      });

      if (!response.ok) {
        throw new InternalServerError(`Failed to get coach swimmers: ${response.status} ${response.statusText}`);
      }

      // Peek at raw text to debug
      const text = await response.text();

      if (!text) {
        throw new InternalServerError("Empty response from get_coach_swimmers API");
      }

      const json = JSON.parse(text);
      return json;

    } catch (error) {
      this.logger.error("Failed to get coach swimmers", error as Error, { coachId, status });
      throw error;
    }
}

  async getSwimmerCoachesApi(swimmerId: number, status: UserConnectionStatus): Promise<NormalResponse> {
    try {
      const formData = new FormData();
      formData.append('swimmer_id', swimmerId.toString());
      formData.append('status', status.toString());

      const response = await fetch(`${this.config.baseUrl}/api/team/get-swimmer-coaches`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to get swimmer coaches');
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to get swimmer coaches', error as Error, { swimmerId, status });
      throw error;
    }
  }

  // Connections (API-based)
  async acceptCoachConnectionFromSwimmerApi(coachId: number, userId: number, accept: boolean): Promise<NormalResponse> {
    try {
      const formData = new FormData();
      formData.append('coach_id', coachId.toString());
      formData.append('user_id', userId.toString());
      formData.append('action', accept ? '1' : '2');

      const response = await fetch(`${this.config.baseUrl}/api/team/accept-coach-connection-from-swimmer`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to accept coach connection from swimmer');
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to accept coach connection from swimmer', error as Error, { coachId, userId, accept });
      throw error;
    }
  }

  async acceptCoachConnectionFromCoachApi(userId: number, coachId: number, accept: boolean): Promise<NormalResponse> {
    try {
      const formData = new FormData();
      formData.append('user_id', userId.toString());
      formData.append('coach_id', coachId.toString());
      formData.append('action', accept ? '1' : '2');

      const response = await fetch(`${this.config.baseUrl}/api/team/accept-coach-connection-from-coach`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to accept coach connection from coach');
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to accept coach connection from coach', error as Error, { userId, coachId, accept });
      throw error;
    }
  }

  async acceptSwimmerConnectionApi(userId: number, friendId: number, accept: boolean): Promise<NormalResponse> {
    try {
      const formData = new FormData();
      formData.append('user_id', userId.toString());
      formData.append('friend_id', friendId.toString());
      formData.append('action', accept ? '1' : '2');

      const response = await fetch(`${this.config.baseUrl}/api/team/accept-swimmer-connection`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to accept swimmer connection');
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to accept swimmer connection', error as Error, { userId, friendId, accept });
      throw error;
    }
  }

  async swimmerConnectToSwimmerApi(userId: number, friendId: number): Promise<NormalResponse> {
    try {
      const formData = new FormData();
      formData.append('user_id', userId.toString());
      formData.append('friend_id', friendId.toString());

      const response = await fetch(`${this.config.baseUrl}/api/team/swimmer-connect-to-swimmer`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to connect swimmer to swimmer');
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to connect swimmer to swimmer', error as Error, { userId, friendId });
      throw error;
    }
  }

  // Team Invitations (API-based)
  async acceptJoinTeamApi(userId: number, teamId: number, accept: boolean): Promise<NormalResponse> {
    try {
      const formData = new FormData();
      formData.append('user_id', userId.toString());
      formData.append('team_id', teamId.toString());
      formData.append('action', accept ? '1' : '2');

      const response = await fetch(`${this.config.baseUrl}/api/team/accept-join-team`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to accept join team');
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to accept join team', error as Error, { userId, teamId, accept });
      throw error;
    }
  }

  async acceptInviteTeamApi(userId: number, coachId: number, teamId: number, accept: boolean): Promise<NormalResponse> {
    try {
      const formData = new FormData();
      formData.append('user_id', userId.toString());
      formData.append('coach_id', coachId.toString());
      formData.append('team_id', teamId.toString());
      formData.append('action', accept ? '1' : '2');

      const response = await fetch(`${this.config.baseUrl}/api/team/accept-invite-team`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to accept invite team');
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to accept invite team', error as Error, { userId, coachId, teamId, accept });
      throw error;
    }
  }

  async inviteToTeamApi(coachId: number, userId: number, teamId: number): Promise<NormalResponse> {
    try {
      const formData = new FormData();
      formData.append('coach_id', coachId.toString());
      formData.append('user_id', userId.toString());
      formData.append('team_id', teamId.toString());

      const response = await fetch(`${this.config.baseUrl}/api/team/invite-to-team`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to invite to team');
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to invite to team', error as Error, { coachId, userId, teamId });
      throw error;
    }
  }

  // Friend Management (API-based)
  async getUserFriendSwimmersApi(userId: number, friendStatus: UserConnectionStatus, teamId: number, teamStatus: TeamMemberStatus): Promise<NormalResponse> {
    try {
      const formData = new FormData();
      formData.append('user_id', userId.toString());
      formData.append('friend_status', friendStatus.toString());
      formData.append('team_id', teamId.toString());
      formData.append('team_status', teamStatus.toString());

      const response = await fetch(`${this.config.baseUrl}/api/team/get-user-friend-swimmers`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to get user friend swimmers');
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to get user friend swimmers', error as Error, { userId, friendStatus, teamId, teamStatus });
      throw error;
    }
  }

  // Notifications (API-based)
  async fetchNotificationsApi(userId: number): Promise<NormalResponse> {
    try {
      const formData = new FormData();
      formData.append('user_id', userId.toString());

      const response = await fetch(`${this.config.baseUrl}/api/team/fetch-notifications`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch notifications');
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to fetch notifications', error as Error, { userId });
      throw error;
    }
  }

  async clearNotificationsApi(userId: number): Promise<NormalResponse> {
    try {
      const formData = new FormData();
      formData.append('user_id', userId.toString());

      const response = await fetch(`${this.config.baseUrl}/api/team/clear-notifications`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to clear notifications');
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to clear notifications', error as Error, { userId });
      throw error;
    }
  }

  async deleteNotificationApi(userId: number, notificationId: number): Promise<NormalResponse> {
    try {
      const formData = new FormData();
      formData.append('user_id', userId.toString());
      formData.append('notif_id', notificationId.toString());

      const response = await fetch(`${this.config.baseUrl}/api/team/delete-notification`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to delete notification');
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to delete notification', error as Error, { userId, notificationId });
      throw error;
    }
  }

  // Team Posts (API-based)
  async fetchTeamPostsApi(teamId: number, userId: number, coachId: number, page: number): Promise<NormalResponse> {
    try {
      const formData = new FormData();
      formData.append('team_id', teamId.toString());
      formData.append('user_id', userId.toString());
      formData.append('coach_id', coachId.toString());
      formData.append('page', page.toString());

      const response = await fetch(`${this.config.baseUrl}/api/team/fetch-team-posts`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch team posts');
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to fetch team posts', error as Error, { teamId, userId, coachId, page });
      throw error;
    }
  }

  async deleteTeamPostApi(postId: number, teamId: number, userId: number): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('team_feed_id', postId.toString());
      formData.append('team_id', teamId.toString());
      formData.append('user_id', userId.toString());

      const response = await fetch(`${this.config.baseUrl}/api/team/delete-team-post`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to delete team post');
      }

      return await response.text();
    } catch (error) {
      this.logger.error('Failed to delete team post', error as Error, { postId, teamId, userId });
      throw error;
    }
  }

  // Image Upload (API-based)
  async uploadTeamImageApi(image: Uint8Array, imageMode: string): Promise<ImageUploadResponse> {
    try {
      const formData = new FormData();
      const arrayBuffer = new ArrayBuffer(image.length);
      const view = new Uint8Array(arrayBuffer);
      view.set(image);
      const blob = new Blob([arrayBuffer], { type: 'image/jpeg' });
      formData.append('image', blob, 'image.jpg');
      formData.append('image_mode', imageMode);

      const response = await fetch(`${this.config.baseUrl}/api/team/upload-team-image`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to upload team image');
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to upload team image', error as Error, { imageMode });
      throw error;
    }
  }
}
